import { useEffect, useState } from 'react';
import { taxPaymentsGateway } from '../index';
import { friendlyErrorMessage } from '../../../shared/friendlyError';
import { ApiError } from '../../identity/types';
import { actionLabels, stateGroup, stateLabels, type TaxPaymentAction, type TaxPaymentDetail, type TaxPaymentState } from '../types';
import { DASH } from '../format';
import { MFA_NEEDED_MESSAGE } from './failures';

const REFUSED_SEPARATION = 'A payment must be approved by someone other than the person who prepared it.';

/** The EPN-48 simulated lifecycle: request approval, approve, submit (simulated), cancel. Retry (a new attempt) is the server-decided Retry Payment in the drawer
 * footer, not a control here. Each control is offered only when the server says the next state is allowed, and the server checks it again. */
function actionsFor(next: TaxPaymentState[]): TaxPaymentAction[] {
  const actions: TaxPaymentAction[] = [];
  if (next.includes('PendingApproval')) actions.push('request-approval');
  if (next.includes('Approved')) actions.push('approve');
  if (next.includes('SimulatedSubmitted')) actions.push('submit-simulated');
  if (next.includes('Cancelled')) actions.push('cancel');
  return actions;
}

function explainActionError(err: unknown): string {
  if (err instanceof ApiError && err.status === 403 && err.code === 'MFA_REQUIRED') return MFA_NEEDED_MESSAGE;
  if (err instanceof ApiError && err.status === 403 && err.message.toLowerCase().includes('someone other than')) return REFUSED_SEPARATION;
  if (err instanceof ApiError && (err.status === 400 || err.status === 409) && err.messageIsServerAuthored) return err.message;
  return friendlyErrorMessage(err, 'That did not work. Nothing was changed.');
}

/**
 * The simulated payment controls and activity of the EPN-48 flow, kept in the Payment Details tab. Simulation only: nothing here sends a payment anywhere. After a
 * change the drawer and the list are re-read from the server (`onChanged`); nothing is shown as changed until the server says so.
 */
export function SimulationControls({ paymentId, refreshKey, onChanged }: { paymentId: string; refreshKey: number; onChanged: () => void }) {
  const [detail, setDetail] = useState<TaxPaymentDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState<TaxPaymentAction | null>(null);
  const [confirming, setConfirming] = useState<TaxPaymentAction | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    taxPaymentsGateway.get(paymentId, controller.signal)
      .then((d) => { if (!controller.signal.aborted) { setDetail(d); setError(null); } })
      .catch((err) => { if (!controller.signal.aborted) setError(friendlyErrorMessage(err, 'Could not load this payment.')); });
    return () => controller.abort();
  }, [paymentId, refreshKey]);

  async function run(action: TaxPaymentAction) {
    setBusy(action);
    setMessage(null);
    try {
      setDetail(await taxPaymentsGateway.act(paymentId, action));
      setConfirming(null);
      onChanged();
    } catch (err) {
      setMessage(explainActionError(err));
      setConfirming(null);
    } finally {
      setBusy(null);
    }
  }

  const attempt = detail?.activeAttempt;
  const actions = attempt ? actionsFor(attempt.allowedNextStates) : [];
  const needsConfirm = (a: TaxPaymentAction) => a === 'submit-simulated' || a === 'cancel';

  return (
    <section className="tpd-section" aria-labelledby={`sim-${paymentId}`}>
      <h4 id={`sim-${paymentId}`} className="tpd-subhead">Simulated payment controls</h4>
      <p className="tpd-banner" role="note">{detail?.simulationNote ?? 'Simulation only. No tax payment is sent to any bank, payment provider or agency.'}</p>
      {!detail && !error && <p role="status">Loading…</p>}
      {error && <p className="error" role="alert">{error}</p>}
      {message && <p className="error" role="alert">{message}</p>}
      {detail && attempt && (
        <>
          <dl className="tpd-facts">
            <dt>Simulation state</dt><dd><span className={`tpd__status tpd__status--${stateGroup[attempt.state]}`}>{stateLabels[attempt.state]}</span></dd>
            <dt>Simulated reference</dt><dd>{attempt.simulatedReference ?? DASH}</dd>
            {attempt.failureKind && (<><dt>Why it did not complete</dt><dd>{attempt.failureKind}{attempt.failureCode ? ` (${attempt.failureCode})` : ''}</dd></>)}
            <dt>Attempts</dt><dd>{detail.attempts.length}</dd>
          </dl>
          {actions.length > 0 ? (
            <div className="tpd-actions">
              {actions.map((a) => confirming === a ? (
                <span key={a} className="tpd-confirm">
                  <span>{a === 'cancel' ? 'Cancel this payment?' : 'Submit this payment in the simulation?'}</span>
                  <button type="button" onClick={() => void run(a)} disabled={busy !== null}>{busy === a ? 'Working…' : `Yes, ${actionLabels[a].toLowerCase()}`}</button>
                  <button type="button" onClick={() => setConfirming(null)} disabled={busy !== null}>Keep it</button>
                </span>
              ) : (
                <button key={a} type="button" onClick={() => (needsConfirm(a) ? setConfirming(a) : void run(a))} disabled={busy !== null}>
                  {busy === a ? 'Working…' : actionLabels[a]}
                </button>
              ))}
            </div>
          ) : (
            <p className="tpd-muted">No simulated step is available for a payment in this state.</p>
          )}
          <h5 className="tpd-subhead">Simulation activity</h5>
          <ol className="tpd-history" aria-label="Payment activity">
            {detail.history.map((h, i) => (
              <li key={`${h.occurredAtUtc}-${i}`}>
                <strong>{stateLabels[h.toState]}</strong>
                <span className="tpd-muted"> · {h.trigger} · {h.occurredAtUtc.slice(0, 16).replace('T', ' ')} UTC</span>
              </li>
            ))}
          </ol>
        </>
      )}
    </section>
  );
}
